import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  staffConfirmLibraryAppointment,
  previewLibraryAppointmentForStaff,
} from '../../services/libraryAppointmentService';
import './LibraryQrScan.scss';

const GUID_REGEX =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
const CODE_PARTS = 4;
const CODE_SEGMENT_LENGTH = 4;
const PUBLIC_CODE_TOTAL_LENGTH = CODE_PARTS * CODE_SEGMENT_LENGTH;
const PUBLIC_CODE_PATTERN = /([A-Z0-9]{4}-){3}[A-Z0-9]{4}/i;
const AUTO_CAMERA = 'auto';
const STATUS_LABELS = {
  Pending: 'Beklemede',
  Confirmed: 'Kullanıcı Onayladı',
  StaffConfirmed: 'Personel Onayladı',
  Cancelled: 'İptal Edildi',
  Expired: 'Süresi Doldu',
};

const extractGuid = (value) => {
  if (!value) return null;
  const match = String(value).match(GUID_REGEX);
  return match ? match[0].toLowerCase() : null;
};

const normalizePublicCode = (value) => {
  if (!value) return null;
  const filtered = String(value).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (filtered.length !== PUBLIC_CODE_TOTAL_LENGTH) {
    return null;
  }

  const segments = [];
  for (let i = 0; i < filtered.length; i += CODE_SEGMENT_LENGTH) {
    segments.push(filtered.slice(i, i + CODE_SEGMENT_LENGTH));
  }

  return segments.join('-');
};

const extractPublicCode = (value) => {
  if (!value) return null;
  const textValue = String(value);
  const hyphenatedMatch = textValue.match(PUBLIC_CODE_PATTERN);
  if (hyphenatedMatch) {
    return normalizePublicCode(hyphenatedMatch[0]);
  }

  return normalizePublicCode(textValue);
};

const LibraryQrScan = () => {
  const reactLiveId = useId();
  const liveRegionId = useMemo(
    () => `qr-live-${reactLiveId.replace(/:/g, '')}`,
    [reactLiveId]
  );
  const scannerRef = useRef(null);
  const liveRegionRef = useRef(null);
  const manualInputRefs = useRef([]);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraId, setCameraId] = useState(AUTO_CAMERA);
  const [cameras, setCameras] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [startingCamera, setStartingCamera] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [manualCodeParts, setManualCodeParts] = useState(
    Array(CODE_PARTS).fill('')
  );
  const [previewData, setPreviewData] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const formatDate = (value, includeTime = false) => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return includeTime
        ? date.toLocaleString('tr-TR')
        : date.toLocaleDateString('tr-TR');
    } catch {
      return value;
    }
  };

  const requestPreview = useCallback(
    async ({ appointmentId, appointmentCode }) => {
      if (!appointmentId && !appointmentCode) {
        setError('Geçerli bir randevu bilgisi bulunamadı.');
        return false;
      }

      setProcessing(true);
      setError('');
      setSuccess('');

      let started = false;

      try {
        const response = await previewLibraryAppointmentForStaff({
          appointmentId,
          appointmentCode,
        });

        if (response?.isSuccess && response.data) {
          setPreviewData(response.data);
          setPendingRequest({ appointmentId, appointmentCode });
          setPreviewVisible(true);
          started = true;
        } else {
          setError(
            response?.resultMessage ||
              'Randevu bilgileri getirilemedi. Lütfen tekrar deneyin.'
          );
        }
      } catch (err) {
        setError('İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        setProcessing(false);
      }

      return started;
    },
    []
  );

  const handleFinalConfirmation = useCallback(async () => {
    if (!pendingRequest) {
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await staffConfirmLibraryAppointment(pendingRequest);

      if (response?.isSuccess) {
        setSuccess(
          response.resultMessage || 'Randevu personel tarafından onayland?.'
        );
        setPreviewVisible(false);
        setPreviewData(null);
        setPendingRequest(null);
        setManualCodeParts(Array(CODE_PARTS).fill(''));
        manualInputRefs.current[0]?.focus();
        return;
      }

      setError(
        response?.resultMessage ||
          'Randevu onaylanamadı. Lütfen tekrar deneyin.'
      );
    } catch (err) {
      setError('İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setProcessing(false);
    }
  }, [pendingRequest]);

  const handleCancelPreview = useCallback(() => {
    setPreviewVisible(false);
    setPreviewData(null);
    setPendingRequest(null);
  }, []);

  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        await scanner.stop();
      } catch {
        // ignore
      }
      try {
        await scanner.clear();
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        setCameras(devices || []);
        if (!devices?.length) {
          return;
        }

        const preferred = devices.find((cam) =>
          /back|rear|environment/i.test(cam.label || '')
        );

        if (preferred?.id) {
          setCameraId(preferred.id);
        } else {
          setCameraId(devices[0].id || AUTO_CAMERA);
        }
      })
      .catch(() => setCameras([]));

    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const handleLiveScan = useCallback(
    async (decodedText) => {
      await stopCamera();
      const publicCode = extractPublicCode(decodedText);
      if (publicCode) {
        await requestPreview({ appointmentCode: publicCode });
        return;
      }

      const guid = extractGuid(decodedText);
      await requestPreview({ appointmentId: guid });
    },
    [stopCamera, requestPreview]
  );

  const startCamera = async () => {
    if (processing || cameraActive || startingCamera) {
      return;
    }

    setError('');
    setSuccess('');
    setStartingCamera(true);

    await stopCamera();

    const regionElement = liveRegionRef.current;
    if (!regionElement) {
      setError('Kamera alanı hazırlanamadı. Sayfayı yenileyip tekrar deneyin.');
      setStartingCamera(false);
      return;
    }

    regionElement.innerHTML = '';

    const html5QrCode = new Html5Qrcode(liveRegionId);
    scannerRef.current = html5QrCode;

    const useAuto = cameraId === AUTO_CAMERA;
    const preferredBackCamera = cameras.find((cam) =>
      /back|rear|environment/i.test(cam.label || '')
    );

    const buildConstraints = (id) =>
      id ? { deviceId: { exact: id } } : { facingMode: 'environment' };

    const startWithCamera = async (id) => {
      await html5QrCode.start(
        buildConstraints(id),
        { fps: 10, qrbox: 250 },
        (decodedText) => {
          if (!processing) {
            handleLiveScan(decodedText);
          }
        },
        () => {}
      );
    };

    try {
      if (useAuto) {
        await startWithCamera(preferredBackCamera?.id);
        if (preferredBackCamera?.id) {
          setCameraId(preferredBackCamera.id);
        }
      } else {
        await startWithCamera(cameraId);
      }
      setCameraActive(true);
    } catch (err) {
      if (useAuto && !preferredBackCamera?.id) {
        try {
          await startWithCamera(undefined);
          setCameraActive(true);
        } catch (retryError) {
          setError(
            'Kamera başlatılamadı. Tarayıcı izinlerini kontrol ederek tekrar deneyin.'
          );
          await stopCamera();
        }
      } else {
        setError(
          'Kamera başlatılamadı. Tarayıcı izinlerini kontrol ederek tekrar deneyin.'
        );
        await stopCamera();
      }
    } finally {
      setStartingCamera(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (manualCodeParts.some((part) => part.length !== CODE_SEGMENT_LENGTH)) {
      setError('Lütfen kodun tüm bölümlerini doldurun.');
      return;
    }

    const combinedCode = normalizePublicCode(manualCodeParts.join(''));
    if (!combinedCode) {
      setError('Kod formatı geçerli değil.');
      return;
    }

    const successResult = await requestPreview({
      appointmentCode: combinedCode,
    });

    if (successResult) {
      setManualCodeParts(Array(CODE_PARTS).fill(''));
      manualInputRefs.current[0]?.focus();
    }
  };

  const handleManualCodeChange = (index, rawValue) => {
    const sanitized = rawValue
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .slice(0, CODE_SEGMENT_LENGTH);

    setManualCodeParts((prev) => {
      const next = [...prev];
      next[index] = sanitized;
      return next;
    });

    if (sanitized.length === CODE_SEGMENT_LENGTH && index < CODE_PARTS - 1) {
      manualInputRefs.current[index + 1]?.focus();
    }
  };

  const handleManualCodePaste = (index, event) => {
    const pasted =
      event.clipboardData?.getData('text') ||
      window.clipboardData?.getData('text');
    if (!pasted) {
      return;
    }

    event.preventDefault();
    const filtered = pasted.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!filtered) {
      return;
    }

    setManualCodeParts((prev) => {
      const next = [...prev];
      let segmentIndex = index;
      let pointer = 0;

      while (segmentIndex < CODE_PARTS && pointer < filtered.length) {
        next[segmentIndex] = filtered
          .slice(pointer, pointer + CODE_SEGMENT_LENGTH)
          .toUpperCase();
        pointer += CODE_SEGMENT_LENGTH;
        segmentIndex += 1;
      }

      return next;
    });

    const filledSegments = Math.ceil(
      Math.min(filtered.length, PUBLIC_CODE_TOTAL_LENGTH) / CODE_SEGMENT_LENGTH
    );
    const focusIndex = Math.min(index + filledSegments - 1, CODE_PARTS - 1);
    window.requestAnimationFrame(() => {
      manualInputRefs.current[focusIndex]?.focus();
    });
  };

  const handleManualCodeKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !manualCodeParts[index] && index > 0) {
      event.preventDefault();
      manualInputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="library-qr-scan">
      <div className="page-header">
        <h2>Randevu QR Onayı</h2>
      </div>

      {(error || success) && (
        <div className={`alert ${error ? 'alert-error' : 'alert-success'}`}>
          {error || success}
        </div>
      )}

      <section className="scan-card">
        <div className="live-scan">
          <div className="live-header">
            <h3>Anlık Kamera</h3>
            {cameras.length > 0 && (
              <select
                value={cameraId}
                onChange={(e) => setCameraId(e.target.value)}
                disabled={cameraActive || processing || startingCamera}
              >
                <option value={AUTO_CAMERA}>Varsayılan (arka kamera)</option>
                {cameras.map((cam) => (
                  <option value={cam.id} key={cam.id}>
                    {cam.label || 'Kamera'}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className={`live-view ${cameraActive ? 'active' : ''}`}>
            <div
              id={liveRegionId}
              ref={liveRegionRef}
              className="live-feed"
              aria-hidden="true"
            />
            {!cameraActive && !startingCamera && (
              <div className="live-placeholder">
                <span>Kamerayı başlatın</span>
              </div>
            )}
            {startingCamera && (
              <div className="live-placeholder">
                <span>Kamera hazırlanıyor...</span>
              </div>
            )}
          </div>
          <div className="live-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={cameraActive ? stopCamera : startCamera}
              disabled={processing || startingCamera || (!cameraActive && cameras.length === 0)}
            >
              {cameraActive
                ? 'Kamerayı Durdur'
                : startingCamera
                ? 'Kamera Açılıyor...'
                : 'Kamerayı Başlat'}
            </button>
            {cameras.length === 0 && (
              <p className="hint">
                Kullanılabilir kamera bulunamadı. Lütfen aşağıdaki kod girişini
                kullanın.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="manual-card">
        <h3>Manuel Onay</h3>
        <form className="manual-form" onSubmit={handleManualSubmit}>
          <div className="code-inputs">
            {manualCodeParts.map((value, index) => (
              <input
                key={index}
                ref={(el) => {
                  manualInputRefs.current[index] = el;
                }}
                type="text"
                inputMode="text"
                autoComplete="off"
                maxLength={CODE_SEGMENT_LENGTH}
                placeholder="0000"
                value={value}
                onChange={(e) => handleManualCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleManualCodeKeyDown(index, e)}
                onPaste={(e) => handleManualCodePaste(index, e)}
              />
            ))}
          </div>
          <button className="btn btn-secondary" type="submit" disabled={processing}>
            Onayla
          </button>
        </form>
        <p className="manual-hint">
          QR kod yerine doğrulama kodu
          girerek de randevuyu onaylayabilirsiniz.
        </p>
      </section>

      {previewVisible && previewData && (
        <div className="qr-modal-overlay">
          <div className="qr-modal">
            <div className="qr-modal-header">
              <h3>Randevu Bilgileri</h3>
              <button
                type="button"
                className="qr-modal-close"
                aria-label="Önizlemeyi kapat"
                onClick={handleCancelPreview}
              >
                &times;
              </button>
            </div>
            <div className="qr-modal-body">
              <div className="qr-modal-grid">
                <div>
                  <span>Ad Soyad</span>
                  <strong>{previewData.fullName}</strong>
                </div>
                <div>
                  <span>T.C. Kimlik</span>
                  <strong>{previewData.identityNumber}</strong>
                </div>
                <div>
                  <span>E-Posta</span>
                  <strong>{previewData.email || '-'}</strong>
                </div>
                <div>
                  <span>Telefon</span>
                  <strong>{previewData.phone || '-'}</strong>
                </div>
                <div>
                  <span>Kütüphane</span>
                  <strong>{previewData.libraryName || previewData.libraryId}</strong>
                </div>
                <div>
                  <span>Randevu Tarihi</span>
                  <strong>{formatDate(previewData.appointmentDate)}</strong>
                </div>
                <div>
                  <span>Masa</span>
                  <strong>{previewData.seatNumber}</strong>
                </div>
                <div>
                  <span>Randevu Kodu</span>
                  <strong>{previewData.publicCode}</strong>
                </div>
                <div>
                  <span>İlk Oluşturma</span>
                  <strong>{formatDate(previewData.createDate, true)}</strong>
                </div>
              </div>

              <div
                className={`qr-status-pill status-${(previewData.status || '').toLowerCase()}`}
              >
                {STATUS_LABELS[previewData.status] || previewData.status}
              </div>

              {previewData.notes && (
                <p className="qr-modal-note">
                  <strong>Not:</strong> {previewData.notes}
                </p>
              )}
            </div>
            <div className="qr-modal-actions">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleCancelPreview}
                disabled={processing}
              >
                İptal
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFinalConfirmation}
                disabled={processing}
              >
                {processing ? 'Onaylanıyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryQrScan;





